import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import { ArrowLeft, User, Shield, Clock, LogOut, Trash2 } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Profile() {
  const navigate = useNavigate();
  const { user, token, logout, updateSettings } = useAuth();
  const [name, setName] = useState(user?.name || '');
  const [isAnonymous, setIsAnonymous] = useState(user?.is_anonymous || false);
  const [saving, setSaving] = useState(false);
  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(true);

  useEffect(() => {
    const fetchHistory = async () => {
      try {
        const response = await axios.get(`${API}/history`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setHistory(response.data);
      } catch (error) {
        console.error('Error fetching history:', error);
      } finally {
        setLoadingHistory(false);
      }
    };
    
    if (token) {
      fetchHistory();
    }
  }, [token]);

  const handleSaveSettings = async () => {
    setSaving(true);
    try {
      await updateSettings({ name, is_anonymous: isAnonymous });
      toast.success('Settings saved!');
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteHistoryItem = async (historyId) => {
    try {
      await axios.delete(`${API}/history/${historyId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setHistory(history.filter(h => h.id !== historyId));
      toast.success('History item deleted');
    } catch (error) {
      toast.error('Failed to delete history item');
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
    toast.success('Signed out successfully');
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  return (
    <div className="min-h-screen pb-safe" data-testid="profile-page">
      <div className="max-w-lg mx-auto px-6 pt-8">
        {/* Header */}
        <button 
          onClick={() => navigate('/dashboard')}
          className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors"
          data-testid="back-btn"
        >
          <ArrowLeft className="h-5 w-5" />
          <span>Back</span>
        </button>

        <div className="mt-6 page-enter">
          <h1 className="font-heading text-3xl font-semibold text-foreground">
            Settings
          </h1>
        </div>

        {/* Profile Section */}
        <div className="mt-8 card-soft page-enter stagger-1">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-3 bg-secondary rounded-full">
              <User className="h-6 w-6 text-primary" />
            </div>
            <div>
              <h2 className="font-heading font-semibold text-foreground">Profile</h2>
              <p className="text-sm text-muted-foreground">{user?.email}</p>
            </div>
          </div>

          <div className="space-y-4">
            <div>
              <Label htmlFor="name" className="text-foreground font-medium">
                Display Name
              </Label>
              <Input
                id="name"
                data-testid="name-input"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="input-field mt-2"
                placeholder="Your name"
              />
            </div>
          </div>
        </div>

        {/* Privacy Section */}
        <div className="mt-6 card-soft page-enter stagger-2">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-3 bg-secondary rounded-full">
              <Shield className="h-6 w-6 text-primary" />
            </div>
            <h2 className="font-heading font-semibold text-foreground">Privacy</h2>
          </div>

          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium text-foreground">Anonymous Mode</p>
              <p className="text-sm text-muted-foreground">
                Hide your name in all interactions
              </p>
            </div>
            <Switch
              data-testid="anonymous-toggle"
              checked={isAnonymous}
              onCheckedChange={setIsAnonymous}
            />
          </div>

          <div className="mt-6 p-4 bg-secondary/50 rounded-xl">
            <p className="text-sm text-muted-foreground">
              🔒 When anonymous mode is on, your conversations are not linked to your account.
            </p>
          </div>
        </div>

        {/* Save Button */}
        <div className="mt-6 page-enter stagger-2">
          <Button
            data-testid="save-settings-btn"
            onClick={handleSaveSettings}
            className="btn-primary w-full"
            disabled={saving}
          >
            {saving ? 'Saving...' : 'Save Settings'}
          </Button>
        </div>

        {/* Subscription Tier */}
        <div className="mt-6 card-soft bg-secondary/50 page-enter stagger-3">
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium text-foreground">Current Plan</p>
              <p className="text-sm text-muted-foreground capitalize">
                {user?.subscription_tier || 'Free'} Tier
              </p>
            </div>
            <span className="px-3 py-1 bg-primary/10 text-primary text-sm rounded-full font-medium">
              Active
            </span>
          </div>
          <p className="mt-3 text-xs text-muted-foreground">
            Premium features coming soon!
          </p>
        </div>

        {/* History Section */}
        <div className="mt-8 page-enter stagger-3">
          <div className="flex items-center gap-3 mb-4">
            <Clock className="h-5 w-5 text-primary" />
            <h2 className="font-heading font-semibold text-foreground">Recent Scripts</h2>
          </div>

          {loadingHistory ? (
            <div className="text-center py-8 text-muted-foreground">Loading...</div>
          ) : history.length === 0 ? (
            <div className="card-soft bg-muted text-center py-8">
              <p className="text-muted-foreground">No saved scripts yet</p>
              <p className="text-sm text-muted-foreground mt-1">
                Complete a scenario to see your history
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {history.slice(0, 5).map((item) => (
                <div key={item.id} className="card-soft flex items-center justify-between">
                  <div>
                    <p className="font-medium text-foreground capitalize">
                      {item.scenario_type}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      {formatDate(item.created_at)}
                    </p>
                  </div>
                  <button
                    onClick={() => handleDeleteHistoryItem(item.id)}
                    className="p-2 text-muted-foreground hover:text-destructive transition-colors"
                    data-testid={`delete-history-${item.id}`}
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Logout */}
        <div className="mt-10 page-enter stagger-4">
          <Button
            data-testid="logout-btn"
            variant="ghost"
            onClick={handleLogout}
            className="w-full text-destructive hover:text-destructive hover:bg-destructive/10"
          >
            <LogOut className="h-5 w-5 mr-2" />
            Sign Out
          </Button>
        </div>

        <div className="h-8" />
      </div>
    </div>
  );
}
